import {
  DownTimeDashboard,
  DowntimeFeatureProviders,
  DowntimePlantIdProvider,
  DowntimeStoppagesDetails,
  DowntimeStoppagesDetailsForCode,
  DowntimeStoppagesStats,
  EquipmentKpiStats
} from '@features/downtime-dashboard'
import {expertSystemUsageRateFeatureTranslations} from '@features/expert-system-usage-rate'
import {performanceReportsFeatureTranslations} from '@features/performance-reports'
import {stoppageAnalysisFeatureTranslations} from '@features/stoppage-analysis'
import {Permission} from '@hconnect/apiclient'
import {PLANT_STOPPAGE_LIST_ROUTE} from '@hconnect/common/consts'
import {appUrl} from '@hconnect/common/hproduce/config'
import {MAINTAIN_HOST} from '@hconnect/common/hproduce/config/maintain'
import {FeatureTranslationsLoader} from '@hconnect/common/localization-loaders'
import {InvertedSurfaceProvider} from '@hconnect/common/styles'
import {Error403Page, Error404Page, useIsMobile} from '@hconnect/uikit/src/lib2'
import {TFunction} from 'i18next'
import {
  generatePath,
  useLocation,
  Link as RouterLink,
  Navigate,
  Route,
  useNavigate,
  useParams
} from 'react-router'

import {PlantStatusPageReferer} from '../shared/consts'
import {ConfigProviderWrapper} from '../shared/hooks/useConfig'
import {useNavAccessContext} from '../shared/hooks/useNavAccessContext'
import {TranslationPrefixProvider} from '../shared/hooks/useTranslationPrefix'
import {
  AREA_EXPERT_SYSTEM_USAGE_RATE,
  COUNTRY_EXPERT_SYSTEM_USAGE_RATE,
  EXPERT_SYSTEM_USAGE_RATE,
  MAJOR_OUTAGE_LEAD_DOWNTIME,
  MAJOR_OUTAGE_LEAD_DOWNTIME_KILN_STATS,
  PLANT_MAJOR_OUTAGE_LEAD_DASHBOARD,
  MAJOR_OUTAGE_LEAD_DOWNTIME_STOPPAGES,
  MAJOR_OUTAGE_LEAD_DOWNTIME_STOPPAGES_DETAILS,
  MAJOR_OUTAGE_LEAD_DOWNTIME_STOPPAGES_DETAILS_FOR_CODE,
  MAJOR_OUTAGE_LEAD_STOPPAGE_ANALYSIS,
  OPTIMIZATION_SPECIALIST_PLANT_KPI,
  OPTIMIZATION_SPECIALIST_PLANT_KPI_STOPPAGE_ANALYSIS,
  OPTIMIZATION_SPECIALIST_STOPPAGE_ANALYSIS,
  PLANT_EXPERT_SYSTEM_USAGE_RATE,
  PROCESS_ENGINEER_PLANT_KPI,
  PROCESS_ENGINEER_PLANT_KPI_STOPPAGE_ANALYSIS,
  PROCESS_ENGINEER_STOPPAGE_ANALYSIS,
  REGION_EXPERT_SYSTEM_USAGE_RATE,
  RELIABILITY_ENGINEER_PLANT_KPI,
  RELIABILITY_ENGINEER_PLANT_KPI_STOPPAGE_ANALYSIS,
  RELIABILITY_ENGINEER_STOPPAGE_ANALYSIS
} from '../shared/routes'
import {Context} from '../shared/types'

import {Error500Page} from './components/Error500Page'
import {DefaultShell} from './containers'
import {GlobalPageRedirectWrapper} from './containers/GlobalPageRedirectWrapper'
import {hasViewTechnicalKpisPermission} from './helpers'
import {GlobalContextProvider} from './hooks/useGlobalContext'
import {HierarchyProvider} from './hooks/useHierarchy'
import {HProduceToursProvider} from './hooks/useHProduceTours'
import {AreaStatus} from './pages/AreaStatus'
import {ExpertSystemUsageRate} from './pages/ExpertSystemUsageRate'
import {KilnMillStatus} from './pages/KilnMillStatus'
import {LocationStatus} from './pages/LocationStatus'
import {NotAvailablePage} from './pages/NotAvailablePage'
import {PerformancePlantSelect} from './pages/PerformancePlantSelect'
import {PerformanceStatusWrapper} from './pages/PerformanceStatusWrapper'
import {PlantStatus} from './pages/PlantStatus'
import {StoppageAnalysis} from './pages/StoppageAnalysis'
import {
  AREA_KPI,
  DOWNTIME,
  ERROR_403,
  ERROR_500,
  GLOBAL_KILN_PERFORMANCE,
  LOCATION_KPI,
  PLANT_DOWNTIME,
  PLANT_DOWNTIME_STATS,
  PLANT_DOWNTIME_STOPPAGES,
  PLANT_DOWNTIME_STOPPAGES_DETAILS,
  PLANT_DOWNTIME_STOPPAGES_DETAILS_FOR_CODE,
  PLANT_KPI,
  GLOBAL_KILN_STATUS,
  STOPPAGE_ANALYSIS_STATUS,
  UNAUTHORIZED,
  AREA_KPI_STATUS,
  LOCATION_KPI_STATUS,
  PLANT_KPI_STATUS,
  STOPPAGE_ANALYSIS,
  PLANT_PERFORMANCE
} from './routing'

const Wrapper: React.FC<{
  context: Context
  permissions: Permission[]
  children: React.ReactNode
}> = ({children, permissions, context}) => (
  <HProduceToursProvider>
    <GlobalContextProvider initialState={{permissions, ...context}}>
      <HierarchyProvider>
        <TranslationPrefixProvider>
          <DefaultShell context={context}>{children}</DefaultShell>
        </TranslationPrefixProvider>
      </HierarchyProvider>
    </GlobalContextProvider>
  </HProduceToursProvider>
)

const EsWrapper: React.FC<{
  context: Context
  permissions: Permission[]
  children: React.ReactNode
}> = ({children, permissions, context}) => (
  <HProduceToursProvider>
    <FeatureTranslationsLoader translations={expertSystemUsageRateFeatureTranslations}>
      <GlobalContextProvider initialState={{permissions, ...context}}>
        <TranslationPrefixProvider>
          <ExpertSystemGuard>
            <DefaultShell context={context}>{children}</DefaultShell>
          </ExpertSystemGuard>
        </TranslationPrefixProvider>
      </GlobalContextProvider>
    </FeatureTranslationsLoader>
  </HProduceToursProvider>
)

const StoppageAnalysisWrapper: React.FC<{
  children: React.ReactNode
}> = ({children}) => (
  <FeatureTranslationsLoader translations={stoppageAnalysisFeatureTranslations}>
    <ConfigProviderWrapper>{children}</ConfigProviderWrapper>
  </FeatureTranslationsLoader>
)

const PlantStatusWrapper: React.FC<{
  children: React.ReactNode
}> = ({children}) => (
  <FeatureTranslationsLoader translations={performanceReportsFeatureTranslations}>
    <ConfigProviderWrapper>{children}</ConfigProviderWrapper>
  </FeatureTranslationsLoader>
)

const DowntimeDashboardWrapper: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {plantId} = useParams<{plantId: string}>()

  if (!plantId) {
    throw new Error('Missing plantId prop')
  }

  return (
    <DowntimePlantIdProvider plantId={plantId}>
      <DowntimeFeatureProviders>
        <InvertedSurfaceProvider>{children}</InvertedSurfaceProvider>
      </DowntimeFeatureProviders>
    </DowntimePlantIdProvider>
  )
}

const getHproduceRcfaStoppageListUrl = (plantId: string) =>
  `${appUrl(MAINTAIN_HOST)}${generatePath(PLANT_STOPPAGE_LIST_ROUTE, {plantId})}`

const PlantDowntimeDashboardPage = () => {
  const {plantId} = useParams<{plantId: string}>()

  if (!plantId) {
    throw new Error('Missing plantId prop')
  }

  return <DownTimeDashboard stoppageListHref={getHproduceRcfaStoppageListUrl(plantId)} />
}

const MajorOutageLeadDowntimeDashboardWrapper = () => {
  const {plantId} = useParams<{plantId: string}>()
  const navigate = useNavigate()

  if (!plantId) {
    throw new Error('Missing plantId prop')
  }

  return (
    <DowntimeDashboardWrapper>
      <DownTimeDashboard
        stoppageListHref={getHproduceRcfaStoppageListUrl(plantId)}
        onGoBackToMOL={() =>
          void navigate(generatePath(PLANT_MAJOR_OUTAGE_LEAD_DASHBOARD, {plantId}))
        }
      />
    </DowntimeDashboardWrapper>
  )
}

const LocationGuard: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {allowedLocations} = useNavAccessContext()

  if (allowedLocations.length === 0) {
    return <PerformancePlantSelect path={PLANT_PERFORMANCE} />
  }
  return <>{children}</>
}

const ExpertSystemGuard: React.FC<{children: React.ReactNode}> = ({children}) => {
  const location = useLocation()
  const isMobile = useIsMobile()

  if (!isMobile && location.pathname !== EXPERT_SYSTEM_USAGE_RATE) {
    return <Navigate to={EXPERT_SYSTEM_USAGE_RATE} replace />
  }
  return <>{children}</>
}

export const getPerformanceDashboardRoutes = (
  context: Context,
  permissions: Permission[],
  t: TFunction
) => {
  const showViewTechnicalKpisPermission = hasViewTechnicalKpisPermission(permissions)

  return [
    <Route
      key="performance-dashboard-error-403"
      path={ERROR_403}
      element={
        <Wrapper context={context} permissions={permissions}>
          <Error403Page
            caption={t('performanceDashboard.errorPage.accessDenied')}
            description={t('performanceDashboard.errorPage.accessDeniedDescription')}
            onLogout={context.onLogout}
            logoutCaption={t('performanceDashboard.logout')}
          />
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-error-500"
      path={ERROR_500}
      element={
        <Wrapper context={context} permissions={permissions}>
          <Error500Page
            caption={t('performanceDashboard.errorPage.serverError')}
            description={t('performanceDashboard.errorPage.serverErrorDescription')}
            copyLogCaption={t('performanceDashboard.errorPage.serverErrorCopyLog')}
          />
        </Wrapper>
      }
    />,

    /* Performance Routes */
    <Route
      key="performance-dashboard-plant-status"
      path={PLANT_KPI}
      element={
        <Wrapper context={context} permissions={permissions}>
          <PlantStatusWrapper>
            <PlantStatus referer={PlantStatusPageReferer.PERFORMANCE} />
          </PlantStatusWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="reliability-engineer-plant-status"
      path={RELIABILITY_ENGINEER_PLANT_KPI}
      element={
        <Wrapper context={context} permissions={permissions}>
          <PlantStatusWrapper>
            <PlantStatus referer={PlantStatusPageReferer.RELIABILITY_ENGINEER_DASHBOARD} />
          </PlantStatusWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="process-engineer-plant-status"
      path={PROCESS_ENGINEER_PLANT_KPI}
      element={
        <Wrapper context={context} permissions={permissions}>
          <PlantStatusWrapper>
            <PlantStatus referer={PlantStatusPageReferer.PROCESS_ENGINEER_DASHBOARD} />
          </PlantStatusWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="optimization-specialist-plant-status"
      path={OPTIMIZATION_SPECIALIST_PLANT_KPI}
      element={
        <Wrapper context={context} permissions={permissions}>
          <PlantStatusWrapper>
            <PlantStatus referer={PlantStatusPageReferer.OPTIMIZATION_SPECIALIST_DASHBOARD} />
          </PlantStatusWrapper>
        </Wrapper>
      }
    />,

    <Route
      key="performance-dashboard-global-kiln-performance"
      path={GLOBAL_KILN_PERFORMANCE}
      element={
        <Wrapper context={context} permissions={permissions}>
          <LocationGuard>
            <PerformanceStatusWrapper />
          </LocationGuard>
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-area-kpi"
      path={AREA_KPI}
      element={
        <Wrapper context={context} permissions={permissions}>
          <PerformanceStatusWrapper />
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-location-kpi"
      path={LOCATION_KPI}
      element={
        <Wrapper context={context} permissions={permissions}>
          <PerformanceStatusWrapper />
        </Wrapper>
      }
    />,
    <Route
      key="status-dashboard-stoppage-analysis"
      path={STOPPAGE_ANALYSIS_STATUS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <StoppageAnalysisWrapper>
            <StoppageAnalysis />
          </StoppageAnalysisWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-stoppage-analysis"
      path={STOPPAGE_ANALYSIS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <StoppageAnalysisWrapper>
            <StoppageAnalysis />
          </StoppageAnalysisWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="process-engineer-dashboard-stoppage-analysis"
      path={PROCESS_ENGINEER_STOPPAGE_ANALYSIS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <StoppageAnalysisWrapper>
            <StoppageAnalysis />
          </StoppageAnalysisWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="process-engineer-dashboard-plant-status-stoppage-analysis"
      path={PROCESS_ENGINEER_PLANT_KPI_STOPPAGE_ANALYSIS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <StoppageAnalysisWrapper>
            <StoppageAnalysis />
          </StoppageAnalysisWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="reliability-engineer-dashboard-stoppage-analysis"
      path={RELIABILITY_ENGINEER_STOPPAGE_ANALYSIS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <StoppageAnalysisWrapper>
            <StoppageAnalysis />
          </StoppageAnalysisWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="reliability-engineer-dashboard-plant-status-stoppage-analysis"
      path={RELIABILITY_ENGINEER_PLANT_KPI_STOPPAGE_ANALYSIS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <StoppageAnalysisWrapper>
            <StoppageAnalysis />
          </StoppageAnalysisWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="optimization-specialist-dashboard-stoppage-analysis"
      path={OPTIMIZATION_SPECIALIST_STOPPAGE_ANALYSIS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <StoppageAnalysisWrapper>
            <StoppageAnalysis />
          </StoppageAnalysisWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="major-outage-lead-dashboard-stoppage-analysis"
      path={MAJOR_OUTAGE_LEAD_STOPPAGE_ANALYSIS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <StoppageAnalysisWrapper>
            <StoppageAnalysis />
          </StoppageAnalysisWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="optimization-specialist-plant-status-stoppage-analysis"
      path={OPTIMIZATION_SPECIALIST_PLANT_KPI_STOPPAGE_ANALYSIS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <StoppageAnalysisWrapper>
            <StoppageAnalysis />
          </StoppageAnalysisWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-unauthorized"
      path={UNAUTHORIZED}
      element={
        <Wrapper context={context} permissions={permissions}>
          <NotAvailablePage />
        </Wrapper>
      }
    />,

    <Route
      key="performance-dashboard-performance-redirect"
      path={'/'}
      element={<Navigate to={GLOBAL_KILN_PERFORMANCE} />}
    />,

    /* End of Performance Routes */

    /* Status Routes */
    <Route
      key="performance-dashboard-global-kiln-status"
      path={GLOBAL_KILN_STATUS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <LocationGuard>
            <GlobalPageRedirectWrapper>
              <KilnMillStatus />
            </GlobalPageRedirectWrapper>
          </LocationGuard>
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-area-status"
      path={AREA_KPI_STATUS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <AreaStatus />
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-location-status"
      path={LOCATION_KPI_STATUS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <LocationStatus />
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-plant-status"
      path={PLANT_KPI_STATUS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <PlantStatusWrapper>
            <PlantStatus referer={PlantStatusPageReferer.STATUS} />
          </PlantStatusWrapper>
        </Wrapper>
      }
    />,
    /* End of Status Routes */

    /* Downtime Routes */
    <Route
      key="performance-dashboard-downtime"
      path={DOWNTIME}
      element={
        <Wrapper context={context} permissions={permissions}>
          <PerformancePlantSelect path={PLANT_DOWNTIME} />
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-plant-downtime"
      path={PLANT_DOWNTIME}
      element={
        <Wrapper context={context} permissions={permissions}>
          <DowntimeDashboardWrapper>
            <PlantDowntimeDashboardPage />
          </DowntimeDashboardWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="major-outage-lead-plant-downtime"
      path={MAJOR_OUTAGE_LEAD_DOWNTIME}
      element={
        <Wrapper context={context} permissions={permissions}>
          <MajorOutageLeadDowntimeDashboardWrapper />
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-plant-downtime-kiln-stats"
      path={PLANT_DOWNTIME_STATS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <DowntimeDashboardWrapper>
            <EquipmentKpiStats />
          </DowntimeDashboardWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="major-outage-lead-plant-downtime-kiln-stats"
      path={MAJOR_OUTAGE_LEAD_DOWNTIME_KILN_STATS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <DowntimeDashboardWrapper>
            <EquipmentKpiStats />
          </DowntimeDashboardWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-plant-downtime-stoppages"
      path={PLANT_DOWNTIME_STOPPAGES}
      element={
        <Wrapper context={context} permissions={permissions}>
          <DowntimeDashboardWrapper>
            <DowntimeStoppagesStats />
          </DowntimeDashboardWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="major-outage-lead-plant-downtime-stoppages"
      path={MAJOR_OUTAGE_LEAD_DOWNTIME_STOPPAGES}
      element={
        <Wrapper context={context} permissions={permissions}>
          <DowntimeDashboardWrapper>
            <DowntimeStoppagesStats />
          </DowntimeDashboardWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-plant-downtime-stoppages-details"
      path={PLANT_DOWNTIME_STOPPAGES_DETAILS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <DowntimeDashboardWrapper>
            <DowntimeStoppagesDetails />
          </DowntimeDashboardWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="major-outage-lead-plant-downtime-stoppages-details"
      path={MAJOR_OUTAGE_LEAD_DOWNTIME_STOPPAGES_DETAILS}
      element={
        <Wrapper context={context} permissions={permissions}>
          <DowntimeDashboardWrapper>
            <DowntimeStoppagesDetails />
          </DowntimeDashboardWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="performance-dashboard-plant-downtime-stoppages-details-for-code"
      path={PLANT_DOWNTIME_STOPPAGES_DETAILS_FOR_CODE}
      element={
        <Wrapper context={context} permissions={permissions}>
          <DowntimeDashboardWrapper>
            <DowntimeStoppagesDetailsForCode />
          </DowntimeDashboardWrapper>
        </Wrapper>
      }
    />,
    <Route
      key="major-outage-lead-plant-downtime-stoppages-details-for-code"
      path={MAJOR_OUTAGE_LEAD_DOWNTIME_STOPPAGES_DETAILS_FOR_CODE}
      element={
        <Wrapper context={context} permissions={permissions}>
          <DowntimeDashboardWrapper>
            <DowntimeStoppagesDetailsForCode />
          </DowntimeDashboardWrapper>
        </Wrapper>
      }
    />,

    /* End of Downtime Routes */

    /* Expert system usage rate routes */
    ...(showViewTechnicalKpisPermission
      ? [
          <Route
            key="expert-system-usage-rate"
            path={EXPERT_SYSTEM_USAGE_RATE}
            element={
              <EsWrapper context={context} permissions={permissions}>
                <ExpertSystemUsageRate />
              </EsWrapper>
            }
          />,
          <Route
            key="area-expert-system-usage-rate"
            path={AREA_EXPERT_SYSTEM_USAGE_RATE}
            element={
              <EsWrapper context={context} permissions={permissions}>
                <ExpertSystemUsageRate />
              </EsWrapper>
            }
          />,
          <Route
            key="region-expert-system-usage-rate"
            path={REGION_EXPERT_SYSTEM_USAGE_RATE}
            element={
              <EsWrapper context={context} permissions={permissions}>
                <ExpertSystemUsageRate />
              </EsWrapper>
            }
          />,
          <Route
            key="country-expert-system-usage-rate"
            path={COUNTRY_EXPERT_SYSTEM_USAGE_RATE}
            element={
              <EsWrapper context={context} permissions={permissions}>
                <ExpertSystemUsageRate />
              </EsWrapper>
            }
          />,
          <Route
            key="plant-expert-system-usage-rate"
            path={PLANT_EXPERT_SYSTEM_USAGE_RATE}
            element={
              <EsWrapper context={context} permissions={permissions}>
                <ExpertSystemUsageRate />
              </EsWrapper>
            }
          />
        ]
      : []),
    /* End of expert system usage rate routes */

    <Route
      key="performance-dashboard-error-404"
      path={'*'}
      element={
        <Wrapper context={context} permissions={permissions}>
          <Error404Page
            caption={t('performanceDashboard.errorPage.notFound')}
            description={t('performanceDashboard.errorPage.notFoundDescription')}
            goToCaption={t('performanceDashboard.errorPage.goToHome')}
            goToLink={'/'}
            RouterLink={RouterLink}
          />
        </Wrapper>
      }
    />
  ]
}
